/**
 * 1NOW-11137: operator rich text keeps the colours the FMS editor saved.
 *
 * The insurance instructions an operator writes (RentalCover, Allianz) carry
 * inline `color:` declarations, black and red in the case that was reported.
 * On a dark tenant (#0F0F0F) black is 1.1:1 and the red is 3.98:1, so renters
 * could not read how to buy the insurance they were being asked to select.
 * The rest of the page is in the tenant's text colour; this note was not,
 * because the inline colour beats the inherited one.
 *
 * Any inline text colour that falls below AA (4.5:1) against what it sits on is
 * removed, so the text takes the tenant's own text colour. Colours that already
 * read are left alone: an operator's emphasis survives wherever it can be read.
 * A link that loses its colour is underlined, or it would read as plain text.
 * The ground is the tenant's page colour, or the element's own inline
 * background-color (and its children's) when the operator set one.
 */
const NAMED: Record<string, number[]> = {
  black: [0, 0, 0], white: [255, 255, 255], red: [255, 0, 0], green: [0, 128, 0],
  blue: [0, 0, 255], yellow: [255, 255, 0], gray: [128, 128, 128], grey: [128, 128, 128],
  orange: [255, 165, 0], purple: [128, 0, 128], navy: [0, 0, 128], maroon: [128, 0, 0],
  teal: [0, 128, 128], silver: [192, 192, 192], lime: [0, 255, 0], aqua: [0, 255, 255],
  fuchsia: [255, 0, 255], olive: [128, 128, 0],
};

export const parseColor = (value?: string | null): number[] | null => {
  const v = String(value ?? "").trim().toLowerCase();
  if (!v) return null;
  if (NAMED[v]) return NAMED[v];
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(v);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split("").map((c) => c + c).join("") : hex[1];
    const n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const fn = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/.exec(v);
  if (fn) {
    const c = [Number(fn[1]), Number(fn[2]), Number(fn[3])];
    return c.every((x) => x >= 0 && x <= 255) ? c : null;
  }
  return null; // transparent, currentcolor, var(), anything we cannot judge
};

const channel = (v: number) => {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};
const luminance = (c: number[]) => 0.2126 * channel(c[0]) + 0.7152 * channel(c[1]) + 0.0722 * channel(c[2]);
export const contrastRatio = (a: number[], b: number[]) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

export const fitInlineColors = (html: string, pageColor?: string | null): string => {
  if (!html || typeof document === "undefined" || !/color/i.test(html)) return html;
  const page = parseColor(pageColor) ?? [0, 0, 0]; // the provider's own default page colour is #000000
  const tpl = document.createElement("template");
  tpl.innerHTML = html;

  const walk = (el: Element, ground: number[]) => {
    const style = (el as HTMLElement).style;
    let here = ground;
    if (style) {
      here = parseColor(style.backgroundColor) ?? ground;
      const fg = parseColor(style.color);
      if (fg && contrastRatio(fg, here) < 4.5) {
        style.removeProperty("color");
        if (el.tagName === "A") style.textDecoration = "underline";
        if (!el.getAttribute("style")) el.removeAttribute("style");
      }
    }
    Array.from(el.children).forEach((child) => walk(child, here));
  };
  Array.from(tpl.content.children).forEach((child) => walk(child, page));
  return tpl.innerHTML;
};
